<script setup>
import { useStore } from 'vuex';
import { computed, watch, ref } from 'vue';
import { SectionComponent, ChartDoughnutComponent } from '@/components';
import { useScreenSize } from '@/composables';

const store = useStore();

const { screenSize } = useScreenSize();

const isMobile = computed(() => screenSize.value === 'mobile');

// get equipment data
const equipmentCount = computed(() => {
  return store.state.myLoadsStore.equipmentCount;
});

const isLoading = computed(() => {
  return store.state.myLoadsStore.equipmentTableVehicles.isLoading;
});

const hasLoaded = computed(() => {
  return store.state.myLoadsStore.equipmentTableVehicles.hasLoaded;
});

const showContent = ref(true);

watch(
  isMobile,
  (newVal) => {
    if (newVal) {
      showContent.value = false;
    }
  },
  { immediate: true }
);

const chartColors = computed(() =>
  equipmentCount.value
    ? ['rgba(82, 199, 115, 1)', 'rgba(255, 119, 28, 1)', 'rgba(221, 56, 19, 1)']
    : [
        'rgba(31, 36, 46, 0.14)',
        'rgba(31, 36, 46, 0.14)',
        'rgba(31, 36, 46, 0.14)',
      ]
);

const chartStyle = computed(() => {
  return {
    size: 'h-44 w-44',
    colors: chartColors.value,
  };
});

const chartData = computed(() => {
  return [
    {
      id: 'eld_connected_vehicles',
      value: equipmentCount.value
        ? equipmentCount.value.eld_connected_vehicles
        : 1,
      label: 'Connected',
    },
    {
      id: 'eld_exempt_vehicles',
      value: equipmentCount.value
        ? equipmentCount.value.eld_exempt_vehicles
        : 1,
      label: 'ELD Exempt',
    },
    {
      id: 'unresolved_vehicles',
      value: equipmentCount.value
        ? equipmentCount.value.unresolved_vehicles
        : 1,
      label: 'No ELD Connected',
    },
  ];
});
</script>

<template>
  <SectionComponent
    custom-height="h-full"
    :custom-width="isMobile ? 'w-full' : 'w-fit'"
    :custom-padding="isMobile ? 'p-0' : 'p-4'"
    custom-gap="gap-4"
    title="Equipment"
    sub-title="Status of Vehicles"
    :is-accordion="isMobile"
    :show-content="showContent"
    @header-click="
      () => {
        if (!isMobile) return;
        showContent = !showContent;
      }
    "
  >
    <template #rightHeader>
      <RouterLink
        v-if="!isMobile"
        to="/carrier/equipment"
        class="custom-button default-button flex h-11 w-fit items-center justify-center gap-4 rounded-[5px] text-sm outline outline-1 outline-offset-[-1px] outline-steel-40"
      >
        Resolve Issues
      </RouterLink>
      <div
        v-else
        class="flex items-center gap-4"
      >
        <img
          v-if="equipmentCount?.unresolved_vehicles"
          src="@/assets/alert-square-red.svg"
          class="flex items-center gap-4"
        />
        <img
          src="@/assets/chevron-right-dark-gray.svg"
          class="w-6"
          :class="showContent ? '-rotate-90 transform' : ''"
          alt="chevron-right"
        />
      </div>
    </template>
    <template #content>
      <transition
        enter-active-class="transition duration-25 ease-out"
        enter-from-class="transform scale-95 opacity-0"
        enter-to-class="transform scale-100 opacity-100"
        leave-active-class="transition duration-75 ease-out"
        leave-from-class="transform scale-100 opacity-100"
        leave-to-class="transform scale-95 opacity-0"
      >
        <div
          class="flex w-full cursor-default flex-col gap-2"
          :class="{ 'p-4': isMobile }"
          v-if="showContent"
        >
          <div
            v-if="
              hasLoaded &&
              (equipmentCount === null || equipmentCount.total_count === 0)
            "
            class="flex flex-col items-center justify-center gap-2"
          >
            <img
              src="@/assets/power-unit.svg"
              alt="truck icon"
              class="h-4 w-4 object-cover"
            />
            <p class="text-xs font-normal leading-4 text-steel-900">
              No equipment found
            </p>
          </div>
          <ChartDoughnutComponent
            v-else
            chart-id="equipment-doughnut-chart"
            link="/equipment"
            :is-loading="isLoading"
            :style="chartStyle"
            :data="chartData"
            :title="equipmentCount?.total_vehicles?.toString()"
            :hasInnerShadow="true"
            sub-title="Total Vehicles"
          />
          <div class="flex w-full flex-col items-start justify-center gap-2">
            <div class="flex w-full items-center gap-2">
              <div class="flex flex-1 items-center justify-start gap-2">
                <div class="h-2.5 w-2.5 rounded-full bg-forest-400"></div>
                <div class="text-xs font-normal leading-4 text-steel-900">
                  ELD Connected
                </div>
              </div>
              <div
                class="self-end text-xs font-semibold leading-4 tracking-tight text-steel-900"
              >
                {{ equipmentCount?.eld_connected_vehicles }}
              </div>
            </div>
            <div
              class="h-0 self-stretch outline outline-1 outline-offset-[-0.50px] outline-steel-200"
            ></div>
            <div class="flex w-full items-center gap-2">
              <div class="flex flex-1 items-center justify-start gap-2">
                <div class="orange-dot h-2.5 w-2.5 rounded-full"></div>
                <div class="text-xs font-normal leading-4 text-steel-900">
                  ELD Exempt
                </div>
              </div>
              <div
                class="self-end text-xs font-semibold leading-4 tracking-tight text-steel-900"
              >
                {{ equipmentCount?.eld_exempt_vehicles }}
              </div>
            </div>
            <div
              class="h-0 self-stretch outline outline-1 outline-offset-[-0.50px] outline-steel-200"
            ></div>
            <div class="flex w-full items-center gap-2">
              <div class="flex flex-1 items-center justify-start gap-2">
                <div class="h-2.5 w-2.5 rounded-full bg-rose-700"></div>
                <div class="text-xs font-normal leading-4 text-steel-900">
                  Not ELD Connected
                </div>
              </div>
              <div
                class="self-end text-xs font-semibold leading-4 tracking-tight text-steel-900"
              >
                {{ equipmentCount?.unresolved_vehicles }}
              </div>
            </div>
          </div>
          <RouterLink
            v-if="isMobile"
            to="/carrier/equipment"
            class="custom-button primary-button mt-2 flex h-11 w-full items-center justify-center gap-4 rounded-[5px] text-sm outline outline-1 outline-offset-[-1px] outline-steel-40"
          >
            Resolve Issues
          </RouterLink>
        </div>
      </transition>
    </template>
  </SectionComponent>
</template>

<style scoped>
.orange-dot {
  background-color: rgba(255, 119, 28, 1);
}
</style>
